import { FC, useCallback, useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { Trans } from 'react-i18next'
import { v4 } from 'uuid'

import { Card, CardContent, CardHeader, Divider, Grid, Switch, Typography } from '@mui/material'

import { Currency } from '../../../../types/Currency'
import { EventEditForm, EventEditFormPricePoints } from '../../types/EventEditForm'
import { PricePoint } from '../../types/PricePoint'
import { PricePointAdder } from './PricePointAdder/PricePointAdder'
import { PricePointTable } from './PricePointTable/PricePointTable'

type PricePointContainerProps = {
  onChange: (pricePoins: EventEditFormPricePoints) => void
  currencies: Currency[] | undefined
  pricePoints: EventEditFormPricePoints
  trackingEventPricePoints?: PricePoint[] | undefined
}

export const PricePointContainer: FC<PricePointContainerProps> = ({ pricePoints, onChange, currencies, trackingEventPricePoints }) => {
  const [copyPricePoint, setCopyPricePoint] = useState(false)
  const [originalPricePoints, setOriginalPricePoints] = useState<EventEditFormPricePoints | null>(null)
  const [copiedKeys, setCopiedKeys] = useState<string[]>([])

  //form.watch is used to keep updated list of pricepoints
  const form = useFormContext<EventEditForm>()
  const formPricePoints = form.watch('event.pricePoints')
  const handleAddPricePoints = useCallback(
    (newPricePoint: { value: string; currencyId: string; description: string }) => {
      const key = v4()
      onChange({
        ...formPricePoints,
        [key]: { value: newPricePoint.value, currencyId: newPricePoint.currencyId, description: newPricePoint.description, key: key },
      })
    },
    [onChange, formPricePoints]
  )
  const handleRemovePricePoint = useCallback(
    (pricePointId: string) => {
      const { [pricePointId]: removablePricePoint, ...rest } = formPricePoints
      onChange(rest)
    },
    [onChange, formPricePoints]
  )

  const convertTrackingEventPricePoints = (list: PricePoint[]): EventEditFormPricePoints => {
    return list.reduce<EventEditFormPricePoints>((acc, item) => {
      const key = v4()
      acc[key] = {
        value: String(item.value ?? ''),
        currencyId: item.currencyId ?? '',
        description: item.description ?? '',
        key,
      }
      return acc
    }, {})
  }

  useEffect(() => {
    const current = form.getValues('event.pricePoints') as EventEditFormPricePoints

    if (copyPricePoint && trackingEventPricePoints?.length) {
      if (!originalPricePoints) setOriginalPricePoints(current)

      const added = convertTrackingEventPricePoints(trackingEventPricePoints)

      const base = originalPricePoints ?? current
      const merged = { ...base, ...added }
      onChange(merged)

      const newKeys = Object.keys(added)
      setCopiedKeys((prev) => Array.from(new Set([...prev, ...newKeys])))
      return
    }

    if (!copyPricePoint && copiedKeys.length) {
      const next = Object.fromEntries(Object.entries(current).filter(([k]) => !copiedKeys.includes(k)))
      onChange(next)
      setCopiedKeys([])
      setOriginalPricePoints(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [copyPricePoint, trackingEventPricePoints, onChange])

  return (
    <>
      <Card>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item sm={3}>
            <CardHeader title={<Trans i18nKey="internal-live-events:price_points" />} />
          </Grid>
          {trackingEventPricePoints?.length && (
            <Grid item sm={9} sx={{ display: 'flex' }}>
              <Switch size="small" checked={copyPricePoint} onChange={() => setCopyPricePoint(!copyPricePoint)} name="active" color="primary" />
              <Typography>
                <Trans i18nKey="internal-common:copy_price_points" />
              </Typography>
            </Grid>
          )}
        </Grid>
        <Divider />
        <PricePointTable pricePoints={pricePoints} currencies={currencies} onRemovePricePoint={handleRemovePricePoint} />
        <CardHeader subheader={<Trans i18nKey="internal-live-events:add_new_price_point" />} sx={{ pb: 1 }} />
        <CardContent>
          <PricePointAdder pricePoints={pricePoints} currencies={currencies} onAddPricePoint={handleAddPricePoints} />
        </CardContent>
      </Card>
    </>
  )
}
